/**
 * CUPOLAS (2026-10-09, docs/bim-plan/29-cupola-study.md) — a lantern, vent cupola or ridge
 * monitor sitting on a roof. The first roof-mounted category.
 *
 * It is FREESTANDING like an object (a single-node `path` anchor + `rotationDeg`, so the
 * shared translate / rotate machinery moves it) and remembers the roof it sits on in
 * `roofId`. Everything about the roof — the seat cut to the pitch, the ridge it is sized
 * from, the attic under it — is read LIVE by the geometry layout (bim-geometry
 * categories/cupola/layout.ts), so editing the roof re-seats the cupola.
 *
 * TYPE = the look (a curated firm standard): plan, base, stage, cornice, roof, cladding, finial.
 * INSTANCE = the job and the size: purpose (light / vent / decorative), size from the ridge or
 * set, the shaft's flare.
 */
import type { CategorySchema } from '../../registry'
import { CUPOLA_PLANS, CUPOLA_BASES, CUPOLA_STAGES, CUPOLA_ROOFS, CUPOLA_CLADS, CUPOLA_FINIALS, VANE_ORNAMENTS, CUPOLA_PURPOSES, CUPOLA_RATIO_DEFAULT } from '../../cupola'

const IN = 1 / 12
const ch = (list: { key: string; label: string }[]) => list.map((x) => ({ value: x.key, label: x.label }))

export const cupolaSchema: CategorySchema = {
  id: 'cupola',
  label: 'Cupola',
  pickRank: 1,
  typeParams: {
    groups: [
      {
        label: 'Form',
        params: [
          { key: 'plan', label: 'Plan', kind: 'choice', default: 'square', choices: ch(CUPOLA_PLANS) },
          { key: 'lengthRatio', label: 'Length (× width)', kind: 'number', default: 2.4, min: 1.2, max: 6, conditionalOn: { param: 'plan', equals: ['monitor'] } },
          // the solid base above the ridge, and the light / louver stage, as fractions of the width
          // (Forest Studio, Montana: a 30" base on a 6'-0" lantern)
          { key: 'baseRatio', label: 'Base Height (× width)', kind: 'number', default: 0.42, min: 0.15, max: 1.2 },
          { key: 'stageRatio', label: 'Stage Height (× width)', kind: 'number', default: 0.55, min: 0.2, max: 1.2 },
        ],
      },
      {
        label: 'Base',
        params: [
          { key: 'base', label: 'Meets the Roof', kind: 'choice', default: 'saddle', choices: ch(CUPOLA_BASES) },
          { key: 'cladding', label: 'Cladding', kind: 'choice', default: 'board', choices: ch(CUPOLA_CLADS) },
        ],
      },
      {
        label: 'Stage',
        params: [
          { key: 'stage', label: 'Sides', kind: 'choice', default: 'glazed', choices: ch(CUPOLA_STAGES) },
          {
            key: 'cornice', label: 'Cornice', kind: 'choice', default: 'full',
            choices: [{ value: 'none', label: 'None (crisp)' }, { value: 'fascia', label: 'Plain Fascia' }, { value: 'full', label: 'Frieze and Crown' }],
          },
        ],
      },
      {
        label: 'Roof',
        params: [
          { key: 'roofForm', label: 'Roof', kind: 'choice', default: 'pyramid', choices: ch(CUPOLA_ROOFS) },
          { key: 'roofRise', label: 'Pitch (rise/12)', kind: 'number', default: 9, min: 2, max: 24 },
          // '' = the firm's standing seam in the palette's Metals color (copper on a copper project)
          { key: 'roofing', label: 'Roofing', kind: 'material', default: '' },
        ],
      },
      {
        label: 'Top',
        params: [
          { key: 'finial', label: 'Top', kind: 'choice', default: 'ball', choices: ch(CUPOLA_FINIALS) },
          { key: 'vaneOrnament', label: 'Weathervane', kind: 'choice', default: 'arrow', choices: ch(VANE_ORNAMENTS), conditionalOn: { param: 'finial', equals: ['vane'] } },
        ],
      },
      {
        label: 'Finish',
        params: [
          // '' = painted trim in the palette's Trim color
          { key: 'bodyMaterial', label: 'Body', kind: 'material', default: '' },
          // '' = the palette's Metals (cladding, finial, vane)
          { key: 'metalMaterial', label: 'Metal', kind: 'material', default: '' },
        ],
      },
    ],
  },
  instanceParams: {
    groups: [
      {
        label: 'Placement',
        params: [
          // single-node anchor path (the object precedent) — the cupola's center in plan
          { key: 'path', label: 'Anchor', kind: 'path2d', default: null },
          // 0 = the cupola's front faces world +y; placement aligns it to the ridge
          { key: 'rotationDeg', label: 'Rotation', kind: 'number', default: 0, min: -360, max: 360, positional: true },
          // the roof it sits on (set at placement; the seat, ridge and attic are read from it)
          { key: 'roofId', label: 'Roof', kind: 'data', default: '', positional: true },
          // the roof-top z at placement — a hint for grips and picking only; the geometry reads the live roof
          { key: 'seatZFt', label: 'Seat', kind: 'data', default: null, hidden: true, positional: true },
          { key: 'sizeHintFt', label: 'Size Hint', kind: 'data', default: null, hidden: true, positional: true },
        ],
      },
      {
        label: 'Purpose',
        params: [
          { key: 'purpose', label: 'Purpose', kind: 'choice', default: 'light', choices: ch(CUPOLA_PURPOSES) },
          {
            key: 'shaft', label: 'Light Shaft', kind: 'choice', default: 'straight',
            choices: [{ value: 'straight', label: 'Straight' }, { value: 'flare', label: 'Flared at the Ceiling' }],
            conditionalOn: { param: 'purpose', equals: ['light'] },
          },
          { key: 'flareFt', label: 'Flare (each side)', kind: 'length', default: 12 * IN, min: 0, max: 6, conditionalOn: { param: 'shaft', equals: ['flare'] } },
        ],
      },
      {
        label: 'Size',
        params: [
          {
            key: 'sizeMode', label: 'Size', kind: 'choice', default: 'ridge',
            choices: [{ value: 'ridge', label: 'From the Ridge' }, { value: 'manual', label: 'Set Width' }],
          },
          // inches of cupola per foot of ridge (JLC 1¼"; range 1–1½")
          { key: 'ratio', label: 'Inches per Foot of Ridge', kind: 'number', default: CUPOLA_RATIO_DEFAULT, min: 0.6, max: 2.5, conditionalOn: { param: 'sizeMode', equals: ['ridge'] } },
          // how many share this ridge (the rule divides the ridge among them)
          { key: 'shareCount', label: 'Cupolas on This Ridge', kind: 'number', default: 1, min: 1, max: 5, conditionalOn: { param: 'sizeMode', equals: ['ridge'] } },
          { key: 'widthFt', label: 'Width', kind: 'length', default: 3, min: 1, max: 12, conditionalOn: { param: 'sizeMode', equals: ['manual'] } },
          // the whole cupola taller or shorter than its proportions (1 = as drawn)
          { key: 'heightScale', label: 'Height (× proportions)', kind: 'number', default: 1, min: 0.6, max: 1.6 },
        ],
      },
    ],
  },
}
